import { Link } from "@tanstack/react-router"
import { AlertTriangle, Check, ChevronRight } from "lucide-react"
import { useRef } from "react"
import type { OrderCounts, OrderListItem, OrdersStateFilter } from "../../services/use-orders"
import { cn } from "../../lib/utils"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { formatDateTime } from "../../lib/format-date"
import { formatCurrency, type ExchangeRates } from "../../lib/money"
import { MarketplaceMark } from "../marketplaces/marketplace-mark"
import { Button } from "../ui/button"
import { Money } from "../ui/money"
import {
  buildDayStats,
  daySubtotal,
  formatDayLabel,
  headlineStage,
  localDayKey,
  orderStages,
  type DayStats,
  type QueueStageTone,
} from "./orders-list-model"

const VIEWS = [
  { value: "all", label: "All" },
  { value: "problem", label: "Problems" },
  { value: "resolved", label: "Resolved" },
] as const

export function OrdersTabs({
  state,
  counts,
  onChange,
}: {
  state: OrdersStateFilter
  counts: OrderCounts | undefined
  onChange: (state: OrdersStateFilter) => void
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  return (
    <div
      role="tablist"
      aria-label="Order views"
      className="mb-4 flex max-w-full gap-1 overflow-x-auto border-b border-border"
    >
      {VIEWS.map((view, index) => (
        <button
          key={view.value}
          ref={(button) => {
            buttons.current[index] = button
          }}
          type="button"
          role="tab"
          id={`orders-tab-${view.value}`}
          aria-controls="orders-panel"
          aria-selected={state === view.value}
          tabIndex={state === view.value ? 0 : -1}
          onClick={() => onChange(view.value)}
          onKeyDown={(event) => {
            const target = {
              ArrowRight: (index + 1) % VIEWS.length,
              ArrowLeft: (index + VIEWS.length - 1) % VIEWS.length,
              Home: 0,
              End: VIEWS.length - 1,
            }[event.key]
            if (target === undefined) {
              return
            }
            event.preventDefault()
            const next = VIEWS[target]
            if (!next) {
              return
            }
            buttons.current[target]?.focus()
            onChange(next.value)
          }}
          className={cn(
            "inline-flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none",
            state === view.value
              ? "border-foreground font-semibold text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          {view.label}

          {counts ? (
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 text-xs tabular-nums",
                view.value === "problem" && counts.problem > 0
                  ? "bg-warning/10 text-warning"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {counts[view.value].toLocaleString()}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  )
}

type ListSearch = {
  state: OrdersStateFilter | undefined
  q: string | undefined
  marketplace: string[] | undefined
}
type OrdersListProps = {
  rows: OrderListItem[]
  rates: ExchangeRates | undefined
  state: OrdersStateFilter
  search: ListSearch
  loading: boolean
  error: Error | null
  hasNextPage: boolean
  fetchingNextPage: boolean
  onLoadMore: () => void
  onRetry: () => void
  clearFilters: (() => void) | undefined
}
const EMPTY_MESSAGE: Record<OrdersStateFilter, string> = {
  all: "No orders match these filters.",
  problem: "No open problems. Every order lines up across the four systems.",
  resolved: "No resolved orders yet.",
}
const GRID = "md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.8fr)_minmax(80px,.55fr)_16px]"

export function OrdersList(props: OrdersListProps) {
  if (props.loading) {
    return (
      <div role="status" aria-label="Loading orders" className="space-y-4 p-5">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-lg bg-muted motion-reduce:animate-none"
          />
        ))}
      </div>
    )
  }
  if (props.error && props.rows.length === 0) {
    return (
      <div role="alert" className="px-5 py-12 text-center text-sm">
        <p>Orders could not be loaded. {props.error.message}</p>

        <Button variant="secondary" className="mt-4 min-h-11" onClick={props.onRetry}>
          Try again
        </Button>
      </div>
    )
  }
  if (!props.rows.length) {
    return (
      <div className="px-5 py-12 text-center text-sm text-muted-foreground">
        <p>{EMPTY_MESSAGE[props.state]}</p>

        {props.clearFilters ? (
          <Button variant="secondary" className="mt-4 min-h-11" onClick={props.clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </div>
    )
  }
  const days = buildDayStats(props.rows)
  // One list item owns an entire day, allowing its header to remain sticky
  // while scrolling all the rows in that day.
  const groups: { key: string; rows: OrderListItem[] }[] = []
  for (const order of props.rows) {
    const key = localDayKey(order.createdAt)
    const last = groups.at(-1)
    if (last?.key === key) {
      last.rows.push(order)
    } else {
      groups.push({ key, rows: [order] })
    }
  }
  return (
    <>
      <div
        className={cn(
          "hidden items-center gap-3 border-b border-border px-4 py-3 text-xs font-medium text-muted-foreground md:grid",
          GRID
        )}
      >
        <span>Order</span>

        <div className="grid grid-cols-4 gap-2">
          {["Marketplace", "Baselinker", "Magento", "OMS"].map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>

        <span className="text-right">Value</span>

        <span className="sr-only">Open</span>
      </div>

      <ol>
        {groups.map((group) => (
          <li key={group.key}>
            <div className="sticky top-14 z-10 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-y border-border bg-background px-4 py-2 text-xs font-medium tabular-nums lg:top-0">
              <span>{formatDayLabel(group.rows[0]?.createdAt ?? "")}</span>

              <DayTotals stats={days.get(group.key)} rates={props.rates} />
            </div>

            <ol>
              {group.rows.map((order) => (
                <li key={`${order.marketplace}:${order.orderId}`}>
                  <OrderRow order={order} rates={props.rates} search={props.search} />
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ol>

      <div className="flex flex-col items-center gap-3 border-t border-border p-4 text-xs text-muted-foreground">
        {props.error ? (
          <div role="alert" className="text-center">
            <p>Orders could not be refreshed. Your loaded rows are still shown.</p>

            <Button variant="ghost" className="mt-2 min-h-11" onClick={props.onRetry}>
              Try again
            </Button>
          </div>
        ) : null}

        {props.hasNextPage ? (
          <Button
            variant="secondary"
            className="min-h-11"
            disabled={props.fetchingNextPage}
            onClick={props.onLoadMore}
          >
            {props.fetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        ) : (
          <span>Newest first · Showing all {props.rows.length}</span>
        )}
      </div>
    </>
  )
}

function OrderRow({
  order,
  rates,
  search,
}: {
  order: OrderListItem
  rates: ExchangeRates | undefined
  search: ListSearch
}) {
  return (
    <Link
      to="/orders/$marketplace/$orderId"
      params={{ marketplace: order.marketplace, orderId: order.orderId }}
      search={search}
      className={cn(
        "group grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-3 border-b border-border/70 px-4 py-4 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none md:items-center",
        GRID,
        order.problemSeverity && !order.resolvedAt
          ? "bg-[color-mix(in_oklch,var(--color-warning)_4%,var(--color-card))] hover:bg-[color-mix(in_oklch,var(--color-warning)_8%,var(--color-card))]"
          : "hover:bg-muted/40"
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        <MarketplaceMark marketplace={order.marketplace} />

        <div className="min-w-0">
          <div
            className="break-words text-sm font-semibold text-foreground [overflow-wrap:anywhere] md:truncate"
            title={order.orderId}
          >
            {order.orderId}
          </div>

          <div className="mt-1 text-xs leading-relaxed text-muted-foreground tabular-nums">
            {`${humanizeMarketplaceId(order.marketplace)} · `}

            <time dateTime={order.createdAt}>{formatDateTime(order.createdAt)}</time>
          </div>

          <div className="hidden md:block">
            <OrderProblem order={order} />
          </div>
        </div>
      </div>

      <div className="col-start-2 row-start-1 text-right md:col-start-3">
        <Money
          amount={order.totalPrice}
          currency={order.currency}
          rates={rates}
          layout="stacked"
          className="text-sm font-semibold"
        />
      </div>

      <div className="col-span-2 empty:hidden md:hidden">
        <OrderProblem order={order} />
      </div>

      <FulfillmentPath order={order} />

      <ChevronRight
        aria-hidden
        className="hidden size-4 text-muted-foreground md:col-start-4 md:row-start-1 md:block"
      />
    </Link>
  )
}

function OrderProblem({ order }: { order: OrderListItem }) {
  if (order.resolvedAt) {
    return (
      <p
        className="mt-2 flex min-w-0 items-start gap-1.5 text-xs text-muted-foreground"
        title={order.resolutionComment ?? undefined}
      >
        <Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />

        <span className="line-clamp-2 md:truncate">
          Resolved{order.resolutionComment ? ` · ${order.resolutionComment}` : ""}
        </span>
      </p>
    )
  }
  const problem = order.problems[0]
  if (!problem) {
    return null
  }
  const critical = order.problemSeverity === "critical"
  return (
    <div
      className={cn(
        "flex min-w-0 items-start gap-2 rounded-lg bg-warning/5 p-3 text-sm md:mt-2 md:bg-transparent md:p-0 md:text-xs",
        critical ? "text-destructive" : "text-warning"
      )}
      title={order.problems.map((item) => item.detail).join("\n")}
    >
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />

      <span className="sr-only">{critical ? "Critical: " : "Warning: "}</span>

      <span className="line-clamp-2 min-w-0 md:truncate">{problem.detail}</span>

      {order.problems.length > 1 ? (
        <span className="shrink-0 rounded-full bg-warning/10 px-1.5 text-xs">
          +{order.problems.length - 1} more
        </span>
      ) : null}
    </div>
  )
}

const DOT: Record<QueueStageTone, string> = {
  good: "bg-success",
  warn: "bg-warning",
  bad: "bg-destructive",
  info: "bg-info",
  muted: "border border-muted-foreground bg-transparent",
}
const TEXT: Record<QueueStageTone, string> = {
  good: "text-success",
  warn: "text-warning",
  bad: "text-destructive",
  info: "text-foreground",
  muted: "text-muted-foreground",
}
function FulfillmentPath({ order }: { order: OrderListItem }) {
  const stages = orderStages(order)
  const headline = headlineStage(stages)
  return (
    <div className="col-span-2 min-w-0 md:col-span-1 md:col-start-2 md:row-start-1">
      <div aria-label="Fulfillment path" className="hidden grid-cols-4 gap-2 md:grid">
        {stages.map((stage, index) => (
          <span
            key={stage.label}
            title={`${stage.label}: ${stage.value}`}
            className="relative min-w-0"
          >
            {index < stages.length - 1 ? (
              <span aria-hidden className="absolute top-[5px] -right-1 left-3 h-px bg-border" />
            ) : null}

            <span
              aria-hidden
              className={cn("relative block size-2.5 rounded-full", DOT[stage.tone])}
            />

            <span className={cn("mt-2 block truncate text-xs", TEXT[stage.tone])}>
              <span className="sr-only">{stage.label}: </span>

              {stage.value}
            </span>
          </span>
        ))}
      </div>

      <div className="flex min-w-0 items-center gap-3 md:hidden">
        <span
          className="flex shrink-0 items-center"
          aria-label={stages.map((stage) => `${stage.label}: ${stage.value}`).join("; ")}
        >
          {stages.map((stage, index) => (
            <span key={stage.label} className="flex items-center" aria-hidden>
              {index > 0 ? <span className="h-px w-3 bg-border" /> : null}

              <span className={cn("size-2 rounded-full", DOT[stage.tone])} />
            </span>
          ))}
        </span>

        {headline ? (
          <span
            className={cn("truncate text-xs", TEXT[headline.tone])}
            title={`${headline.label}: ${headline.value}`}
          >
            {`${headline.label} · ${headline.value}`}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function DayTotals({
  stats,
  rates,
}: {
  stats: DayStats | undefined
  rates: ExchangeRates | undefined
}) {
  if (!stats) {
    return null
  }
  const subtotal = daySubtotal(stats, rates)
  const amount =
    subtotal.eur === null
      ? subtotal.native.map(([currency, value]) => formatCurrency(value, currency)).join(" · ")
      : formatCurrency(subtotal.eur, "EUR")
  return (
    <span className="text-muted-foreground" title="Subtotal for loaded rows">
      {`${stats.count} ${stats.count === 1 ? "order" : "orders"} · ${amount} `}

      <span className="font-normal">loaded</span>
    </span>
  )
}
